/* __next_internal_client_entry_do_not_use__ CodeEmbed,default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import useIsEditing from 'lib/hooks/useIsEditing';
import { getScriptUrl } from 'lib/utils/link-utils';
import { EditingFieldValues } from 'helpers/Editing/EditingFieldValues';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import useIsNormalMode from 'lib/hooks/useIsNormalMode';
import { getTestProps } from 'lib/testing/utils';
import { applyTrustpilotHostHeight, initTrustpilotIfReady, initTrustpilotWithConsent, parseTrustpilotStyleHeight } from 'lib/trustpilot/widget';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
/**
 * Trustpilot markup is rendered straight into the server HTML so the bootstrap can auto-init
 * the widget the moment it loads (~1.5s), instead of having to wait for React to hydrate and
 * inject it. Injecting it in a layout effect meant the bootstrap found zero `.trustpilot-widget`
 * elements when it ran, so every render had to come from a forced `loadFromElement` after
 * hydration — measured at 8s on a cold load and 8-11s on a back/forward restore, where the
 * document is rebuilt from cache and the widget was simply missing until then.
 *
 * React does not touch this subtree while `html` is unchanged, so the iframe Trustpilot injects
 * into it survives re-renders; `suppressHydrationWarning` covers the nodes Trustpilot may add
 * before hydration reaches this element.
 *
 * Height is reserved on first paint to avoid scroll jump when the iframe appears.
 * Init is still owned by `initTrustpilotWithConsent` in CodeEmbed, which polls for the bootstrap
 * and retries — the bootstrap tag has no `onLoad`, and on a cached load it never fires one.
 */ function StableTrustpilotHtml({ html, containerRef, ...rest }) {
    const localRef = useRef(null);
    const htmlRef = useRef(html);
    htmlRef.current = html;
    const reservedHeight = parseTrustpilotStyleHeight(html);
    useLayoutEffect(()=>{
        const node = containerRef?.current ?? localRef.current;
        if (!node) return;
        applyTrustpilotHostHeight(node, htmlRef.current);
        // Safety net only: the markup is server-rendered above, so this should already be present.
        if (!node.querySelector('.trustpilot-widget')) {
            node.innerHTML = htmlRef.current;
        }
        // If bootstrap already loaded (cached / SPA), init immediately. Otherwise the poller
        // started by `initTrustpilotWithConsent` picks the widget up once it appears.
        initTrustpilotIfReady(node);
    }, [
        containerRef
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: (node)=>{
            localRef.current = node;
            if (containerRef) {
                containerRef.current = node;
            }
        },
        style: {
            minHeight: reservedHeight ?? undefined,
            overflowAnchor: 'none'
        },
        suppressHydrationWarning: true,
        dangerouslySetInnerHTML: {
            __html: html
        },
        ...rest
    });
}
const DOTDIGITAL_IFRAME_HEIGHT_BUFFER_PX = 0;
const DOTDIGITAL_IFRAME_MAX_HEIGHT_PX = 10000;
function parseDotdigitalHeightValue(value) {
    const height = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseFloat(value.replace(/px$/i, '').trim()) : Number(value);
    if (Number.isFinite(height) && height > 0 && height <= DOTDIGITAL_IFRAME_MAX_HEIGHT_PX) {
        return Math.ceil(height);
    }
    return null;
}
/**
 * Cookiebot `data-blockingmode=auto` blocks Trustpilot (marketing) until consent,
 * rewriting the script tag to `type="text/plain"` so its `onLoad` never fires and the
 * widget silently stays empty. Whether it wins that race depends on how fast `uc.js`
 * arrives, which is why a cached load can break a page that a cold load renders fine.
 *
 * Trustpilot embeds are therefore exempted here rather than per Sitecore item, so the
 * widget does not depend on an author remembering the attribute in every environment.
 * Cookiebot's own auto-blocking is additionally disabled in local development, where
 * the consent UI never appears on an unauthorized localhost domain.
 */ function sanitizeScriptAttributes({ attrs, scriptId, scriptSrc, htmlCodeHasTrustpilot }) {
    const src = (scriptSrc ?? '').toLowerCase();
    if (src.includes('trustpilot') || htmlCodeHasTrustpilot) {
        return {
            ...attrs,
            'data-cookieconsent': 'ignore'
        };
    }
    const isCookiebot = scriptId === 'Cookiebot' || src.includes('cookiebot.com');
    if (!isCookiebot || "production" !== 'development') {
        return attrs;
    }
    const next = {
        ...attrs
    };
    delete next['data-blockingmode'];
    return next;
}
export const CodeEmbed = (props)=>{
    /**
   ** Refs & Hooks
   **/ const isEditing = useIsEditing();
    const isNormalMode = useIsNormalMode();
    const pathname = usePathname();
    const dotDigitalEle = useRef(null);
    const persistedDotdigitalIframeRef = useRef(null);
    const dotdigitalResizeListenerCleanupRef = useRef(null);
    const trustpilotContainerRef = useRef(null);
    const trustpilotInitCleanupRef = useRef(null);
    const htmlCodeValue = props.fields?.htmlCode?.value ?? '';
    const scriptSourceUrlValue = props.fields?.scriptSourceUrl?.value ?? '';
    const htmlCodeHasTrustpilot = htmlCodeValue.includes('trustpilot-widget');
    const isTrustpilotEmbed = htmlCodeHasTrustpilot || scriptSourceUrlValue.toLowerCase().includes('trustpilot');
    /**
   * Trustpilot renders on mount *and* again whenever Cookiebot's consent state
   * settles. Needed because `data-blockingmode=auto` can neutralise the bootstrap
   * tag — its `onLoad` then never fires, so mount alone is not enough.
   */ useEffect(()=>{
        if (!isTrustpilotEmbed) return;
        trustpilotInitCleanupRef.current?.();
        trustpilotInitCleanupRef.current = initTrustpilotWithConsent(trustpilotContainerRef.current ?? document);
        return ()=>{
            trustpilotInitCleanupRef.current?.();
            trustpilotInitCleanupRef.current = null;
        };
    }, [
        isTrustpilotEmbed
    ]);
    const DOTDIGITAL_FEEDBACK_FORM_SCRIPT_ID = 'feedback-form';
    const DOTDIGITAL_FORM_CONTAINER_ID = 'dotdigital-form-container';
    const isDotdigitalFeedbackForm = props.fields?.scriptId?.value === DOTDIGITAL_FEEDBACK_FORM_SCRIPT_ID;
    const DOTDIGITAL_FEEDBACK_FORM_SOURCE_URL = props.fields?.scriptSourceUrl?.value;
    const params = new URLSearchParams(DOTDIGITAL_FEEDBACK_FORM_SOURCE_URL);
    const DOTDIGITAL_FEEDBACK_FORM_DOMAIN = params.get('domain');
    const DOTDIGITAL_FEEDBACK_FORM_SUBURL = params.get('id');
    const DOTDIGITAL_FEEDBACK_FORM_SUBURL_ARRAY = DOTDIGITAL_FEEDBACK_FORM_SUBURL?.split('/');
    const DOTDIGITAL_IFRAME_ID = DOTDIGITAL_FEEDBACK_FORM_SUBURL_ARRAY?.[0];
    /** Published form path (short URL redirects here). */ const DOTDIGITAL_IFRAME_PAGE_URL = `https://${DOTDIGITAL_FEEDBACK_FORM_DOMAIN}/p/${DOTDIGITAL_FEEDBACK_FORM_SUBURL}`;
    /** Shown until Dotdigital posts its content height via postMessage. */ const DOTDIGITAL_IFRAME_MIN_HEIGHT_PX = 480;
    function isDotdigitalPagesOrigin(origin) {
        try {
            const hostname = new URL(origin).hostname;
            if (/\.dotdigital-pages\.com$/i.test(hostname)) {
                return true;
            }
            return !!DOTDIGITAL_FEEDBACK_FORM_DOMAIN && hostname === DOTDIGITAL_FEEDBACK_FORM_DOMAIN;
        } catch  {
            return false;
        }
    }
    function isDotdigitalMessageForThisForm(messageName) {
        if (!messageName) {
            return true;
        }
        if (!DOTDIGITAL_IFRAME_ID) {
            return true;
        }
        if (messageName === DOTDIGITAL_IFRAME_ID) {
            return true;
        }
        if (DOTDIGITAL_FEEDBACK_FORM_SUBURL?.startsWith(`${messageName}/`)) {
            return true;
        }
        return messageName.startsWith(`${DOTDIGITAL_IFRAME_ID}/`);
    }
    function extractHeightFromDotdigitalMessage(data) {
        if (data == null) {
            return null;
        }
        const directHeight = parseDotdigitalHeightValue(data);
        if (directHeight != null) {
            return directHeight;
        }
        let payload = data;
        if (typeof data === 'string') {
            const parsedHeight = parseDotdigitalHeightValue(data);
            if (parsedHeight != null) {
                return parsedHeight;
            }
            try {
                payload = JSON.parse(data);
            } catch  {
                return null;
            }
            const jsonHeight = parseDotdigitalHeightValue(payload);
            if (jsonHeight != null) {
                return jsonHeight;
            }
        }
        if (typeof payload !== 'object' || payload === null) {
            return null;
        }
        const record = payload;
        const messageName = typeof record.name === 'string' ? record.name : null;
        if (!isDotdigitalMessageForThisForm(messageName)) {
            return null;
        }
        const nestedData = typeof record.data === 'object' && record.data !== null ? record.data : undefined;
        const candidates = [
            record.height,
            record.iframeHeight,
            nestedData?.height,
            nestedData?.iframeHeight
        ];
        for (const candidate of candidates){
            const height = parseDotdigitalHeightValue(candidate);
            if (height != null) {
                return height;
            }
        }
        return null;
    }
    /** Dotdigital reads the parent URL from the iframe hash to enable postMessage resize. */ function buildDotdigitalIframeSrc() {
        if ("object" === 'undefined') {
            return DOTDIGITAL_IFRAME_PAGE_URL;
        }
        const parentPageUrl = window.location.href.split('#')[0];
        return `${DOTDIGITAL_IFRAME_PAGE_URL}#${encodeURIComponent(parentPageUrl)}`;
    }
    function syncDotdigitalIframeSrc(iframe) {
        const nextSrc = buildDotdigitalIframeSrc();
        const currentSrc = iframe.getAttribute('src') ?? '';
        if (currentSrc !== nextSrc) {
            iframe.setAttribute('src', nextSrc);
        }
    }
    function getDotdigitalIframeRenderedHeight(iframe) {
        return Math.ceil(Number.parseFloat(iframe.style.height) || 0);
    }
    function applyDotdigitalIframeLayout(iframe, container) {
        container.style.width = '100%';
        iframe.removeAttribute('height');
        iframe.style.width = '100%';
        iframe.style.display = 'block';
        iframe.style.border = '0';
        iframe.style.overflow = 'hidden';
        const renderedHeight = getDotdigitalIframeRenderedHeight(iframe);
        if (renderedHeight <= DOTDIGITAL_IFRAME_MIN_HEIGHT_PX) {
            iframe.style.minHeight = `${DOTDIGITAL_IFRAME_MIN_HEIGHT_PX}px`;
            if (!iframe.style.height || iframe.style.height === 'auto') {
                iframe.style.height = `${DOTDIGITAL_IFRAME_MIN_HEIGHT_PX}px`;
            }
        }
    }
    function setDotdigitalIframeHeight(iframe, height) {
        const nextHeight = Math.max(getDotdigitalIframeRenderedHeight(iframe), height + DOTDIGITAL_IFRAME_HEIGHT_BUFFER_PX);
        iframe.style.height = `${nextHeight}px`;
        iframe.style.minHeight = '0';
    }
    function ensureDotdigitalResizeListener() {
        if (dotdigitalResizeListenerCleanupRef.current) {
            return;
        }
        const handler = (event)=>{
            if (!isDotdigitalPagesOrigin(event.origin)) {
                return;
            }
            const height = extractHeightFromDotdigitalMessage(event.data);
            if (height == null) {
                return;
            }
            const iframe = persistedDotdigitalIframeRef.current ?? document.getElementById(DOTDIGITAL_IFRAME_ID ?? '');
            if (iframe) {
                setDotdigitalIframeHeight(iframe, height);
            }
        };
        window.addEventListener('message', handler);
        dotdigitalResizeListenerCleanupRef.current = ()=>{
            window.removeEventListener('message', handler);
            dotdigitalResizeListenerCleanupRef.current = null;
        };
    }
    function isDotdigitalFormIframe(iframe) {
        return iframe.id === DOTDIGITAL_IFRAME_ID || iframe.src.includes(`dotdigital-pages.com/p/${DOTDIGITAL_IFRAME_ID}`);
    }
    function getOrCreateDotdigitalIframe() {
        if (persistedDotdigitalIframeRef.current) {
            syncDotdigitalIframeSrc(persistedDotdigitalIframeRef.current);
            return persistedDotdigitalIframeRef.current;
        }
        const existing = document.getElementById(DOTDIGITAL_IFRAME_ID ?? '');
        if (existing instanceof HTMLIFrameElement) {
            persistedDotdigitalIframeRef.current = existing;
            syncDotdigitalIframeSrc(existing);
            return existing;
        }
        const iframe = document.createElement('iframe');
        iframe.id = DOTDIGITAL_IFRAME_ID ?? '';
        iframe.title = 'Feedback form';
        iframe.setAttribute('scrolling', 'no');
        syncDotdigitalIframeSrc(iframe);
        persistedDotdigitalIframeRef.current = iframe;
        return iframe;
    }
    function removeDuplicateDotdigitalIframes(keep) {
        document.querySelectorAll('iframe').forEach((node)=>{
            if (node === keep || !(node instanceof HTMLIFrameElement)) {
                return;
            }
            if (isDotdigitalFormIframe(node)) {
                node.remove();
            }
        });
    }
    function mountDotdigitalIframe(container) {
        // Remove legacy nodes from earlier implementations.
        document.getElementById('dotdigital-form-iframe-holder')?.remove();
        document.getElementById('dotdigital-landing-pages-embedded-script')?.remove();
        const iframe = getOrCreateDotdigitalIframe();
        removeDuplicateDotdigitalIframes(iframe);
        container.querySelectorAll('iframe').forEach((node)=>{
            if (node !== iframe) {
                node.remove();
            }
        });
        if (iframe.parentElement !== container) {
            container.appendChild(iframe);
        }
        applyDotdigitalIframeLayout(iframe, container);
        ensureDotdigitalResizeListener();
        return iframe;
    }
    function detachDotdigitalIframe(iframe) {
        persistedDotdigitalIframeRef.current = iframe;
        iframe.remove();
    }
    useLayoutEffect(()=>{
        if (!isDotdigitalFeedbackForm || typeof document === 'undefined') {
            return;
        }
        const container = dotDigitalEle.current;
        if (!container) {
            return;
        }
        const iframe = mountDotdigitalIframe(container);
        return ()=>{
            detachDotdigitalIframe(iframe);
        };
    }, [
        isDotdigitalFeedbackForm
    ]);
    useLayoutEffect(()=>{
        if (!isDotdigitalFeedbackForm || typeof document === 'undefined') {
            return;
        }
        const iframe = persistedDotdigitalIframeRef.current;
        if (iframe?.isConnected) {
            syncDotdigitalIframeSrc(iframe);
        }
    }, [
        isDotdigitalFeedbackForm,
        pathname
    ]);
    const parseAdditionalTagAttributes = (value)=>{
        if (!value) return {};
        try {
            return Object.fromEntries(new URLSearchParams(value));
        } catch  {
            return {};
        }
    };
    const [scriptAttributesState, setScriptAttributesState] = useState(()=>sanitizeScriptAttributes({
            attrs: parseAdditionalTagAttributes(props.fields?.additionalTagAttributes?.value),
            scriptId: props.fields?.scriptId?.value,
            scriptSrc: props.fields?.scriptSourceUrl?.value,
            htmlCodeHasTrustpilot
        }));
    useEffect(()=>{
        setScriptAttributesState(sanitizeScriptAttributes({
            attrs: parseAdditionalTagAttributes(props.fields?.additionalTagAttributes?.value),
            scriptId: props.fields?.scriptId?.value,
            scriptSrc: props.fields?.scriptSourceUrl?.value,
            htmlCodeHasTrustpilot
        }));
    }, [
        props.fields?.additionalTagAttributes?.value,
        props.fields?.scriptId?.value,
        props.fields?.scriptSourceUrl?.value,
        htmlCodeHasTrustpilot
    ]);
    /**
   ** Life Cycle
   **/ if (!props.fields) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    /**
   ** Rendering
   **/ const htmlCodeContainsScript = /<script\b(?![^>]*\/>)[^>]*>(?:(?!<\/script>)[\s\S])*<\/script\s*>/.test(htmlCodeValue);
    const hasScriptSourceUrl = !!scriptSourceUrlValue;
    const hasScriptId = !!props.fields.scriptId?.value;
    const htmlCodeHasIframe = /<iframe\b/i.test(htmlCodeValue);
    const shouldRenderHtmlCode = !!htmlCodeValue && !(isDotdigitalFeedbackForm && htmlCodeHasIframe);
    const shouldRenderScriptSourceUrl = hasScriptSourceUrl && !isDotdigitalFeedbackForm;
    const shouldRenderInlineScript = !!props.fields.script?.value && !isDotdigitalFeedbackForm;
    if (isEditing) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-component": "authorable/shared/content/codeembed",
            ...editingHydrationProps(isEditing),
            children: [
                htmlCodeContainsScript && /*#__PURE__*/ _jsx(EditingHelpText, {
                    priority: "warning",
                    children: "The HTML Code below contains an inline script which must be removed or it will cause an error when rendering the page. Move the script to the Script field below to allow it to execute properly."
                }),
                hasScriptSourceUrl && !hasScriptId && /*#__PURE__*/ _jsx(EditingHelpText, {
                    priority: "warning",
                    children: "A Script ID is required when a Script Source URL is provided. Please enter a unique ID for the script."
                }),
                /*#__PURE__*/ _jsx(EditingFieldValues, {
                    hasRenderingParams: true,
                    fields: [
                        {
                            label: 'HTML Code',
                            field: props.fields.htmlCode
                        },
                        {
                            label: 'Script Source URL',
                            field: props.fields.scriptSourceUrl
                        },
                        {
                            label: 'Script Source URL ID',
                            field: props.fields.scriptId
                        },
                        {
                            label: 'Script',
                            field: props.fields.script
                        }
                    ]
                })
            ]
        });
    }
    if (props.fields.enabled?.value) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-component": "authorable/shared/content/codeEmbed",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-code-embed-${props?.rendering?.uid}`),
            children: [
                shouldRenderHtmlCode && (htmlCodeHasTrustpilot ? /*#__PURE__*/ _jsx(StableTrustpilotHtml, {
                    html: htmlCodeValue,
                    containerRef: trustpilotContainerRef,
                    ...getTestProps(`html-code`)
                }) : /*#__PURE__*/ _jsx("div", {
                    dangerouslySetInnerHTML: {
                        __html: htmlCodeValue
                    },
                    ...getTestProps(`html-code`)
                })),
                shouldRenderScriptSourceUrl && (isTrustpilotEmbed ? /*
             * Plain async script rather than `next/script`. `next/script` injects the tag
             * client-side after hydration — measured at 8s on Windows, and past 30s on an
             * empty-cache hard reload, where the watchdog below had already given up and the
             * widget stayed blank for good. Emitted here it ships in the server HTML and
             * executes during parse, before DOMContentLoaded, so Trustpilot's own auto-init
             * renders the (also server-rendered) markup without waiting on React at all.
             *
             * React 19 hoists async scripts and dedupes them by `src`, so several Trustpilot
             * embeds on one page cannot load the bootstrap twice. `data-cookieconsent="ignore"`
             * arrives through `scriptAttributesState`, so the Cookiebot exemption is preserved.
             */ /*#__PURE__*/ _jsx("script", {
                    async: true,
                    src: props.fields.scriptSourceUrl?.value,
                    ...Object.fromEntries(Object.entries(scriptAttributesState).filter(([key])=>key !== 'strategy'))
                }) : /*#__PURE__*/ _jsx(Script, {
                    id: props.fields.scriptId?.value,
                    src: props.fields.scriptSourceUrl?.value,
                    strategy: scriptAttributesState.strategy ?? 'afterInteractive',
                    ...Object.fromEntries(Object.entries(scriptAttributesState).filter(([key])=>key !== 'strategy'))
                })),
                shouldRenderInlineScript && /*#__PURE__*/ _jsx(Script, {
                    id: props.id,
                    src: getScriptUrl(isNormalMode, props)
                }),
                isDotdigitalFeedbackForm && /*#__PURE__*/ _jsx("div", {
                    id: DOTDIGITAL_FORM_CONTAINER_ID,
                    ref: dotDigitalEle
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {});
};
export default CodeEmbed;
